// src/pages/Admin/Weighbridge.tsx
/**
 * Timber Management System — Admin › Weighbridge (settings + templates + preview)
 *
 * What you get
 * • Global settings: Watch folder or API mode, poll interval, basic failure behavior.
 * • Templates (per mill): delimiter, has header, date format, field mapping, notes.
 * • Paste-and-preview: paste a small CSV snippet, apply a template, see parsed rows.
 * • Role aware actions (only Admin can edit/delete/duplicate templates & settings).
 * • LocalStorage keys:
 *    - opts:weighbridge:settings:v1
 *    - opts:importTemplates:v1           (array of templates)
 *
 * TODO hooks
 * • Wire background poller to place results into /loads/inbox.
 * • Expand field mapping once you finalize the target schema for loads.
 *
 * Assumptions
 * • Stack: Vite + React + TS + Tailwind. AppShell wraps this page.
 * • Components: AppShell, DataTable, Drawer, Skeleton, EmptyState exist per brief.
 * • Notifications: useNotifications().push({ title, body?, level? }).
 * • Storage helpers: get/set from @/lib/storage; audit via logAudit(payload, role).
 */

import React, { useEffect, useMemo, useState } from 'react'
import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import Drawer from '@/components/Drawer'
import Skeleton from '@/components/Skeleton'
import EmptyState from '@/components/EmptyState'
import { useNotifications } from '@/lib/notifications'
import { get, set } from '@/lib/storage'
import { logAudit } from '@/lib/audit'
import {
  Save,
  Info as InfoIcon,
  Plus,
  Edit,
  Copy,
  Trash2,
  FileJson,
  FlaskConical,
  Play,
  Settings as SettingsIcon,
} from 'lucide-react'

type Role = 'Admin'|'Dispatcher'|'Loader'|'Trucker'|'Accountant'
const actor: Role = get<Role>('role', 'Admin' as Role)

type Mode = 'watch'|'api'
type Settings = {
  mode: Mode
  watchFolder?: string
  apiUrl?: string
  apiToken?: string
  pollIntervalSec: number
  onFailure: 'notify'|'skip'|'stop'
}

type Template = {
  id: string
  name: string
  mill?: string
  notes?: string
  delimiter: 'comma'|'tab'|'semicolon'|'pipe'
  hasHeader: boolean
  dateFormat: string // e.g., 'MM/DD/YYYY HH:mm'
  mapping: Partial<Record<ImportField, string>> // csv column -> semantic
  sampleHeader?: string[] // cached the first line columns (optional)
  updatedAt?: string
}

type ImportField =
  | 'ticket'
  | 'date'
  | 'truck'
  | 'product'
  | 'grossWeight'
  | 'tareWeight'
  | 'netWeight'
  | 'container'
  | 'seal'
  | 'voyage'
  | 'port'
  | 'site'
  | 'mill'

const SETTINGS_KEY = 'opts:weighbridge:settings:v1'
const TMPL_KEY     = 'opts:importTemplates:v1'

const FIELD_OPTIONS: ImportField[] = [
  'ticket','date','truck','product','grossWeight','tareWeight','netWeight',
  'container','seal','voyage','port','site','mill'
]

const DELIM_OPTIONS = [
  { value: 'comma', label: 'Comma (,)' },
  { value: 'tab', label: 'Tab (\\t)' },
  { value: 'semicolon', label: 'Semicolon (;)' },
  { value: 'pipe', label: 'Pipe (|)' },
] as const

function cx(...xs: Array<string | false | null | undefined>) { return xs.filter(Boolean).join(' ') }
function uid() { return (globalThis.crypto && 'randomUUID' in globalThis.crypto) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2) }
const isAdmin = actor === 'Admin'

// ---------- CSV helpers ----------
function toDelimiter(d: Template['delimiter']): string {
  switch (d) {
    case 'comma': return ','
    case 'tab': return '\t'
    case 'semicolon': return ';'
    case 'pipe': return '|'
  }
}

function parseCSV(text: string, delimiter: Template['delimiter'], hasHeader: boolean): { header: string[], rows: string[][] } {
  const delim = toDelimiter(delimiter)
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim().length > 0)
  const split = (line: string) => {
    // a very light splitter, assumes no embedded delimiter inside quotes for now (can be upgraded)
    return line.split(delim).map(s => s.trim())
  }
  if (lines.length === 0) return { header: [], rows: [] }
  let header: string[] = []
  let body: string[][] = []
  if (hasHeader) {
    header = split(lines[0])
    body = lines.slice(1).map(split)
  } else {
    const width = split(lines[0]).length
    header = new Array(width).fill(0).map((_,i) => `col_${i+1}`)
    body = lines.map(split)
  }
  return { header, rows: body }
}

function toNumberSafe(v?: string) {
  if (!v) return 0
  const x = Number(String(v).replace(/[^0-9.\-]/g,''))
  return Number.isFinite(x) ? x : 0
}

// ---------- Component ----------
export default function Weighbridge() {
  const { push } = useNotifications()

  // Settings
  const [settings, setSettings] = useState<Settings>(() => get<Settings>(SETTINGS_KEY, {
    mode: 'watch',
    watchFolder: '',
    apiUrl: '',
    apiToken: '',
    pollIntervalSec: 60,
    onFailure: 'notify',
  }))

  // Templates
  const [templates, setTemplates] = useState<Template[]>(() => get<Template[]>(TMPL_KEY, []))

  // UI
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<Template | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [snippet, setSnippet] = useState('') // pasted CSV
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')

  // Derived
  const selectedTemplate = useMemo(() => templates.find(t => t.id === selectedTemplateId) || null, [templates, selectedTemplateId])

  // Persist helpers
  function persistSettings(next: Settings) {
    set(SETTINGS_KEY, next); setSettings(next)
    push({ title: 'Settings saved' }); logAudit({ action: 'weighbridge:settings:save', next }, actor)
  }
  function persistTemplates(next: Template[]) {
    set(TMPL_KEY, next); setTemplates(next)
  }

  // Template actions
  function openCreate() {
    if (!isAdmin) return
    const t: Template = {
      id: uid(),
      name: 'New Template',
      mill: '',
      delimiter: 'comma',
      hasHeader: true,
      dateFormat: 'MM/DD/YYYY HH:mm',
      mapping: {},
      notes: '',
      sampleHeader: [],
      updatedAt: new Date().toISOString(),
    }
    setEditing(t); setDrawerOpen(true)
  }
  function openEdit(t: Template) {
    if (!isAdmin) return
    setEditing({ ...t }); setDrawerOpen(true)
  }
  function saveTemplate() {
    if (!editing) return
    const next = [...templates]
    const idx = next.findIndex(x => x.id === editing.id)
    const toSave = { ...editing, updatedAt: new Date().toISOString() }
    if (idx === -1) next.unshift(toSave); else next[idx] = toSave
    persistTemplates(next)
    setDrawerOpen(false)
    push({ title: 'Template saved', body: toSave.name })
    logAudit({ action: 'weighbridge:template:save', id: toSave.id, name: toSave.name }, actor)
  }
  function duplicateTemplate(t: Template) {
    if (!isAdmin) return
    const copy: Template = { ...t, id: uid(), name: `${t.name} (Copy)`, updatedAt: new Date().toISOString() }
    persistTemplates([copy, ...templates])
    push({ title: 'Template duplicated', body: copy.name })
    logAudit({ action: 'weighbridge:template:duplicate', id: copy.id, src: t.id }, actor)
  }
  function deleteTemplate(t: Template) {
    if (!isAdmin) return
    if (!confirm(`Delete template "${t.name}"?`)) return
    const next = templates.filter(x => x.id !== t.id)
    persistTemplates(next)
    push({ title: 'Template deleted' })
    logAudit({ action: 'weighbridge:template:delete', id: t.id }, actor)
  }

  // Preview
  const preview = useMemo(() => {
    if (!snippet.trim() || !selectedTemplate) return { cols: [] as string[], rows: [] as any[] }
    const { header, rows } = parseCSV(snippet, selectedTemplate.delimiter, selectedTemplate.hasHeader)
    // cache header into template (non-destructive)
    // (not persisted until save, but helpful if user then opens editor)
    selectedTemplate.sampleHeader = header

    // map rows -> normalized preview
    const m = selectedTemplate.mapping || {}
    const rowsMapped = rows.map((r) => {
      const pick = (field: ImportField) => {
        const colName = m[field]
        if (!colName) return ''
        const idx = header.findIndex(h => h.toLowerCase() === colName.toLowerCase())
        return idx >= 0 ? (r[idx] || '') : ''
      }
      const gross = toNumberSafe(pick('grossWeight'))
      const tare  = toNumberSafe(pick('tareWeight'))
      const net   = toNumberSafe(pick('netWeight')) || (gross - tare)
      return {
        ticket: pick('ticket'),
        date: pick('date'),
        truck: pick('truck'),
        product: pick('product'),
        grossWeight: gross,
        tareWeight: tare,
        netWeight: net,
        container: pick('container'),
        seal: pick('seal'),
        voyage: pick('voyage'),
        port: pick('port'),
        site: pick('site'),
        mill: pick('mill') || (selectedTemplate.mill || ''),
      }
    })
    return {
      cols: ['ticket','date','truck','product','grossWeight','tareWeight','netWeight','container','seal','voyage','port','site','mill'],
      rows: rowsMapped,
    }
  }, [snippet, selectedTemplate])

  // Table for templates
  const cols: Col[] = [
    { key: 'name', label: 'Template' },
    { key: 'mill', label: 'Mill' },
    { key: 'delimiter', label: 'Delimiter' },
    { key: 'header', label: 'Header Row' },
    { key: 'updated', label: 'Updated' },
    { key: 'actions', label: 'Actions' },
  ]
  const rows = templates.map(t => ({
    name: (
      <div className="pl-3 pr-2 min-w-[16rem]">
        <div className="font-medium truncate" title={t.name}>{t.name}</div>
        {t.notes && <div className="text-xs text-slate-500 mt-0.5 truncate" title={t.notes}>{t.notes}</div>}
      </div>
    ),
    mill: <div className="min-w-[10rem] truncate" title={t.mill || ''}>{t.mill || '—'}</div>,
    delimiter: <div className="min-w-[8rem]">{DELIM_OPTIONS.find(d => d.value === t.delimiter)?.label || t.delimiter}</div>,
    header: <div className="min-w-[6rem]">{t.hasHeader ? 'Yes' : 'No'}</div>,
    updated: <div className="min-w-[12rem]">{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : '—'}</div>,
    actions: (
      <div className="flex justify-end gap-2 pr-2 min-w-[12rem]">
        <button className="btn-secondary px-2 py-1" title="Details / Edit" onClick={() => openEdit(t)} disabled={!isAdmin}>
          <Edit className="w-4 h-4" />
        </button>
        <button className="btn-secondary px-2 py-1" title="Duplicate" onClick={() => duplicateTemplate(t)} disabled={!isAdmin}>
          <Copy className="w-4 h-4" />
        </button>
        <button className="btn-danger px-2 py-1" title="Delete" onClick={() => deleteTemplate(t)} disabled={!isAdmin}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    ),
  }))

  // Simulate poller (stubs)
  function simulatePollSuccess() {
    push({ title: 'Weighbridge poll succeeded', body: 'Found 3 new tickets.', level: 'info' })
    logAudit({ action: 'weighbridge:poll:success', count: 3 }, actor)
  }
  function simulatePollFailure() {
    push({ title: 'Weighbridge poll failed', body: 'Connection timeout to API.', level: 'warning' })
    logAudit({ action: 'weighbridge:poll:failure', reason: 'timeout' }, actor)
  }

  return (
    <AppShell title="Weighbridge">
      {/* Settings */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <span className="flex items-center gap-2"><SettingsIcon className="w-4 h-4" /> Ingestion Settings</span>
          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={simulatePollSuccess} title="Simulate successful poll"><Play className="w-4 h-4" /> Success</button>
            <button className="btn-danger" onClick={simulatePollFailure} title="Simulate failed poll"><FlaskConical className="w-4 h-4" /> Failure</button>
          </div>
        </div>

        <div className="card-body grid lg:grid-cols-3 gap-3 items-start">
          <div className="space-y-3">
            <div>
              <label className="text-sm">Mode</label>
              <select
                className="select"
                value={settings.mode}
                onChange={(e) => setSettings(s => ({ ...s, mode: e.target.value as Mode }))}
                disabled={!isAdmin}
              >
                <option value="watch">Watch Folder</option>
                <option value="api">API</option>
              </select>
              <div className="text-xs muted mt-1">
                {settings.mode === 'watch' ? 'Poll a local/network directory for new CSVs.' : 'Fetch CSV or JSON from a mill API.'}
              </div>
            </div>

            {settings.mode === 'watch' ? (
              <div>
                <label className="text-sm">Watch Folder</label>
                <input
                  className="input"
                  placeholder="e.g., \\\\server\\share\\weighbridge"
                  value={settings.watchFolder || ''}
                  onChange={(e) => setSettings(s => ({ ...s, watchFolder: e.target.value }))}
                  disabled={!isAdmin}
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm">API URL</label>
                  <input
                    className="input"
                    placeholder="https://example.com/api/tickets"
                    value={settings.apiUrl || ''}
                    onChange={(e) => setSettings(s => ({ ...s, apiUrl: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div>
                  <label className="text-sm">API Token</label>
                  <input
                    className="input"
                    placeholder="Bearer token…"
                    value={settings.apiToken || ''}
                    onChange={(e) => setSettings(s => ({ ...s, apiToken: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Poll Interval (sec)</label>
                <input
                  type="number"
                  min={10}
                  className="input"
                  value={settings.pollIntervalSec}
                  onChange={(e) => setSettings(s => ({ ...s, pollIntervalSec: Number(e.target.value || 0) }))}
                  disabled={!isAdmin}
                />
              </div>
              <div>
                <label className="text-sm">On Failure</label>
                <select
                  className="select"
                  value={settings.onFailure}
                  onChange={(e) => setSettings(s => ({ ...s, onFailure: e.target.value as Settings['onFailure'] }))}
                  disabled={!isAdmin}
                >
                  <option value="notify">Notify & continue</option>
                  <option value="skip">Skip file & continue</option>
                  <option value="stop">Stop until resolved</option>
                </select>
              </div>
            </div>

            <button className="btn" onClick={() => persistSettings(settings)} disabled={!isAdmin}>
              <Save className="w-4 h-4" /> <span className="ml-1">Save Settings</span>
            </button>
          </div>

          {/* Paste-and-preview */}
          <div className="lg:col-span-2 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Template</label>
                <select
                  className="select w-full"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  <option value="">— Select a template —</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}{t.mill ? ` • ${t.mill}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  className="btn-secondary w-full"
                  onClick={() => {
                    if (!snippet.trim()) { push({ title: 'Paste a CSV snippet first', level: 'warning' }); return }
                    if (!selectedTemplate) { push({ title: 'Choose a template', level: 'warning' }); return }
                    // Nothing to do — preview is computed automatically; this button acts as an affordance.
                    push({ title: 'Preview refreshed' })
                  }}
                >
                  <FileJson className="w-4 h-4" /> Preview
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm">Paste a few CSV lines</label>
              <textarea
                className="input min-h-[8rem] font-mono"
                placeholder="Paste 5–20 lines from a weighbridge CSV file…"
                value={snippet}
                onChange={(e) => setSnippet(e.target.value)}
              />
              <div className="text-xs muted mt-1">
                For large files, the background poller will ingest from the watch folder or API.
              </div>
            </div>

            {/* Preview table */}
            <div className="table-shell">
              {(!selectedTemplate || preview.rows.length === 0) ? (
                <EmptyState title="No preview yet">
                  Choose a template and paste a few CSV lines to see a parsed preview.
                </EmptyState>
              ) : (
                <PreviewTable cols={preview.cols} rows={preview.rows} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <span>Import Templates</span>
          <div className="flex items-center gap-2">
            <button className="btn" onClick={openCreate} disabled={!isAdmin}>
              <Plus className="w-4 h-4" /> New Template
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <Skeleton className="h-48" />
          ) : templates.length === 0 ? (
            <EmptyState title="No templates yet">Create your first per-mill import template.</EmptyState>
          ) : (
            <DataTable columns={cols} rows={rows} filename="weighbridge-templates.csv" storageKey="tbl:weighbridge-templates:v1" />
          )}
        </div>
      </div>

      {/* Template Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing?.name || 'Template'}>
        {editing && (
          <div className="drawer-panel space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm">Template Name</label>
                <input className="input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} disabled={!isAdmin} />
              </div>
              <div>
                <label className="text-sm">Mill (optional)</label>
                <input className="input" value={editing.mill || ''} onChange={(e) => setEditing({ ...editing, mill: e.target.value })} disabled={!isAdmin} />
              </div>
              <div>
                <label className="text-sm">Delimiter</label>
                <select
                  className="select"
                  value={editing.delimiter}
                  onChange={(e) => setEditing({ ...editing, delimiter: e.target.value as Template['delimiter'] })}
                  disabled={!isAdmin}
                >
                  {DELIM_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm">Header Row</label>
                <select
                  className="select"
                  value={editing.hasHeader ? '1' : '0'}
                  onChange={(e) => setEditing({ ...editing, hasHeader: e.target.value === '1' })}
                  disabled={!isAdmin}
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Date Format</label>
                <input
                  className="input"
                  placeholder="MM/DD/YYYY HH:mm"
                  value={editing.dateFormat}
                  onChange={(e) => setEditing({ ...editing, dateFormat: e.target.value })}
                  disabled={!isAdmin}
                />
                <div className="text-xs muted mt-1">Used when parsing dates for loads; align with your source files.</div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm">Notes</label>
                <input className="input" value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} disabled={!isAdmin} />
              </div>
            </div>

            {/* Column mapping */}
            <div className="card p-3 bg-[var(--surface-1)]">
              <div className="text-sm font-medium mb-2">Field Mapping</div>
              <div className="text-xs muted mb-2">
                Map CSV columns to load fields. If you pasted a snippet above and previewed, the header choices will appear here.
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {FIELD_OPTIONS.map((f) => (
                  <div key={f}>
                    <label className="text-xs uppercase tracking-wide">{f}</label>
                    <select
                      className="select w-full"
                      value={editing.mapping[f] || ''}
                      onChange={(e) => setEditing({ ...editing, mapping: { ...editing.mapping, [f]: e.target.value || undefined } })}
                      disabled={!isAdmin}
                    >
                      <option value="">— Unmapped —</option>
                      {(editing.sampleHeader && editing.sampleHeader.length > 0
                        ? editing.sampleHeader
                        : ['Ticket','Date','Truck','Product','Gross','Tare','Net','Container','Seal','Voyage','Port','Site','Mill']
                      ).map(h => (
                        <option key={`${f}-${h}`} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button className="btn btn-secondary" onClick={() => setDrawerOpen(false)}>Close</button>
              <div className="flex-1" />
              <button className="btn" onClick={saveTemplate} disabled={!isAdmin}>
                <Save className="w-4 h-4" /> <span className="ml-1">Save Template</span>
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </AppShell>
  )
}

// ---------- Preview table ----------
function PreviewTable({ cols, rows }: { cols: string[]; rows: any[] }) {
  const tableCols: Col[] = cols.map((c) => ({ key: c, label: headerLabel(c) }))
  const tableRows = rows.map((r) => {
    const obj: Record<string, React.ReactNode> = {}
    for (const c of cols) {
      const v = (r as any)[c]
      const isNum = typeof v === 'number'
      obj[c] = <div className={cx('min-w-[8rem]', isNum ? 'text-right tabular-nums' : '')}>{isNum ? v.toLocaleString() : (v || '—')}</div>
    }
    return obj
  })

  return (
    <DataTable
      columns={tableCols}
      rows={tableRows}
      filename="weighbridge-preview.csv"
      storageKey="tbl:weighbridge-preview:v1"
    />
  )
}

function headerLabel(key: string) {
  switch (key) {
    case 'grossWeight': return 'Gross (lbs)'
    case 'tareWeight': return 'Tare (lbs)'
    case 'netWeight': return 'Net (lbs)'
    default: return key.charAt(0).toUpperCase() + key.slice(1)
  }
}
